import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ColorLogo from '../assets/logos/Color-Logo.png';
import WhiteLogo from '../assets/logos/White-Logo.png';

const HEADER_HEIGHT_MOBILE = 64; // px
const HEADER_HEIGHT_DESKTOP = 72; // px

const Layout = ({ children }) => {
  const { employee: user, logout, theme, toggleTheme, hasPermission, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const allNavigation = [
    // OVERVIEW SECTION
    { name: 'Dashboard', href: '/dashboard', icon: '📊', description: 'Overview and analytics', permission: 'dashboard_view', section: 'OVERVIEW' },
    { name: 'Daily Orders', href: '/todays-orders', icon: '📅', description: 'Today/Tomorrow/Weekly orders', permission: 'orders_view', section: 'OVERVIEW' },
    { name: 'Order Analytics', href: '/order-analytics', icon: '📈', description: 'Order items & bottle analysis', permission: 'orders_view', section: 'OVERVIEW' },
    
    // ORDER MANAGEMENT SECTION
    { name: 'All Orders', href: '/orders', icon: '🛒', description: 'Complete order history', permission: 'orders_view', section: 'ORDER_MANAGEMENT' },
    { name: 'Subscriptions', href: '/subscriptions', icon: '💳', description: 'Manage subscription plans', permission: 'subscriptions_view', section: 'ORDER_MANAGEMENT' },
    { name: 'Scheduler', href: '/scheduler', icon: '⏰', description: 'Subscription automation monitor', permission: 'scheduler_view', section: 'ORDER_MANAGEMENT' },
    
    // DELIVERY & FULFILLMENT SECTION
    { name: 'Delivery Management', href: '/delivery-management-new', icon: '🚚', description: 'Group & assign deliveries', permission: 'delivery_boy_access', rolesOnly: ['admin', 'manager', 'developer'], section: 'DELIVERY' },
    { name: 'Delivery Team', href: '/delivery-boy', icon: '🚴', description: 'Delivery personnel & stats', permission: 'delivery_boy_access', section: 'DELIVERY' },
    { name: 'Bottle Tracking', href: '/bottle-tracking', icon: '🍾', description: 'Customer bottle balances', permission: 'orders_view', section: 'DELIVERY' },
    
    // INVENTORY & PRODUCTS SECTION
    { name: 'Categories', href: '/categories', icon: '🏷️', description: 'Product categories', permission: 'categories_view', section: 'INVENTORY' },
    { name: 'Product Groups', href: '/product-groups', icon: '📁', description: 'Product grouping', permission: 'product_groups_view', section: 'INVENTORY' },
    { name: 'Products', href: '/products', icon: '📦', description: 'Manage inventory', permission: 'products_view', section: 'INVENTORY' },
    { name: 'Special Offers', href: '/special-offers', icon: '🎉', description: 'Promotional offers', permission: 'products_view', section: 'INVENTORY' },
    { name: 'Future Products', href: '/future-products', icon: '🚀', description: 'Upcoming products', permission: 'products_view', section: 'INVENTORY' },
    
    // BUSINESS SETUP SECTION
    { name: 'Companies', href: '/companies', icon: '🏢', description: 'Company management', permission: 'companies_view', section: 'SETUP' },
    { name: 'Customers', href: '/customers', icon: '👥', description: 'Customer database', permission: 'customers_view', section: 'SETUP' },
    { name: 'Customer Subscriptions', href: '/customer-subscriptions', icon: '📚', description: 'View customer subscriptions', permission: 'customers_view', section: 'SETUP' },
    { name: 'Staff', href: '/employees', icon: '👨‍💼', description: 'Staff management & permissions', permission: 'employees_view', section: 'SETUP' },
    
    // ADMINISTRATION SECTION (Hidden from regular users)
    { name: 'System Admin', href: '/system-admin', icon: '⚡', description: 'System monitoring & cron jobs', permission: 'super_admin_access', section: 'ADMIN' },
    { name: 'Permissions', href: '/permissions', icon: '🔐', description: 'Access control management', permission: 'permissions_view', section: 'ADMIN' },
    { name: 'Notifications', href: '/notifications', icon: '🔔', description: 'System alerts & messages', permission: 'notifications_view', section: 'ADMIN' },
    { name: 'Settings', href: '/settings', icon: '⚙️', description: 'System configuration', permission: 'settings_view', section: 'ADMIN' },
  ];

  const navigation = allNavigation.filter(item => {
    const hasPermissionCheck = hasPermission(item.permission);
    const hasRoleCheck = item.rolesOnly ? hasRole(item.rolesOnly) : true;
    return hasPermissionCheck && hasRoleCheck;
  });

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 overflow-x-hidden">
      {/* Header - always at the top */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between px-2 w-full h-[56px] sm:h-[64px] lg:h-[72px]">
        <div className="flex items-center min-w-0">
          {/* Hamburger for mobile */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-1 sm:p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 mr-2"
            aria-label="Open sidebar"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Logo */}
          <Link to="/" className="group flex items-center py-1">
            <img 
              src={theme === 'dark' ? WhiteLogo : ColorLogo} 
              alt="FarmIndie Logo" 
              className="logo-responsive w-auto object-contain group-hover:scale-105 transition-transform duration-300"
            />
          </Link>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Profile Dropdown */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              className="flex items-center space-x-2 p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:block text-sm font-medium">
                {user?.name || 'User'}
              </span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {profileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email || 'user@example.com'}
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    toggleTheme();
                    setProfileDropdownOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {theme === 'dark' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    )}
                  </svg>
                  {theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
                </button>
                
                <button
                  onClick={() => {
                    navigate('/profile/change-password');
                    setProfileDropdownOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                  </svg>
                  Change Password
                </button>
                
                <button
                  onClick={() => {
                    logout();
                    setProfileDropdownOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/70 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - fixed to the left below the header */}
      <aside
        className={`fixed top-[56px] sm:top-[64px] lg:top-[72px] left-0 z-40 w-64 h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)] lg:h-[calc(100vh-72px)] transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0`}
        aria-label="Sidebar"
      >
        <div className="flex flex-col h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-md">
          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-4 overflow-y-auto">
            {(() => {
              const sections = {};
              navigation.forEach(item => {
                const section = item.section || 'OTHER';
                if (!sections[section]) sections[section] = [];
                sections[section].push(item);
              });

              const sectionOrder = ['OVERVIEW', 'ORDER_MANAGEMENT', 'DELIVERY', 'INVENTORY', 'SETUP', 'ADMIN'];
              const sectionLabels = {
                OVERVIEW: '📊 OVERVIEW',
                ORDER_MANAGEMENT: '📦 ORDER MANAGEMENT',
                DELIVERY: '🚚 DELIVERY & FULFILLMENT',
                INVENTORY: '🛍️ INVENTORY & PRODUCTS',
                SETUP: '👥 BUSINESS SETUP',
                ADMIN: '⚙️ ADMINISTRATION'
              };

              return sectionOrder.map(sectionKey => {
                const items = sections[sectionKey];
                if (!items || items.length === 0) return null;

                return (
                  <div key={sectionKey} className="space-y-2">
                    <div className="px-3 py-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {sectionLabels[sectionKey]}
                    </div>
                    <div className="space-y-1">
                      {items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group relative flex items-center px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl transition-all duration-300 hover:scale-105
                            ${isActive(item.href)
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 shadow-sm'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'}
                          `}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span className="text-lg sm:text-xl mr-2 sm:mr-3 transition-transform duration-300 group-hover:scale-110 flex-shrink-0">
                            {item.icon}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm sm:text-base truncate">{item.name}</div>
                            <div className="hidden lg:block text-xs text-gray-500 dark:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 truncate">
                              {item.description}
                            </div>
                          </div>
                          {isActive(item.href) && (
                            <div className="w-2 h-2 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full animate-pulse flex-shrink-0"></div>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </nav>


        </div>
      </aside>

      {/* Main content wrapper - margin left for sidebar on desktop, and margin top for header */}
      <div className="flex-1 flex flex-col pt-[56px] sm:pt-[64px] lg:pt-[72px] lg:ml-64 min-h-0">
        <main className="flex-1 p-2 sm:p-4 md:p-6 overflow-x-auto break-words">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>


    </div>
  );
};

export default Layout; 